import cadex, {
  ModelPrs_SelectionChangedEvent,
  ModelPrs_View3dDimensionObject,
} from '@cadexchanger/web-toolkit'
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faDraftingCompass } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core'
import { ClearOutlined, StraightenOutlined } from '@material-ui/icons'
import { ActionIconButton } from 'components/Layout/ActionIconButton'
import { useEffect, useState } from 'react'
import { MeasurementType } from '../types'
import { useEffectOnce } from '../useEffectOnce'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      with: '70px',
      height: '70px',
      textAlign: 'center',
    },
    container: {
      left: 0,
      position: 'absolute',
      display: 'flex',
      justifyContent: 'flex-end',
      columnGap: '10px',
      transition: '.3s width ease',
      overflow: 'hidden',
      width: '70px',
      // background: 'green',
      '& li': {
        background: theme.palette.background.default,
        borderRadius: '50%',
        opacity: 0,
        transition: '.5s opacity ease',

        '& button': {
          width: '70px',
          height: '70px',
        },

        '&.active': {
          opacity: 1,
          position: 'absolute',
          zIndex: '3',
          background: 'none',
          left: 0,
        },
      },
      '&:hover': {
        width: '230px',
        '& li': {
          opacity: 1,
        },
      },
    },
  })
)

type MeasurementProps = {
  scene: cadex.ModelPrs_Scene
  viewPort: cadex.ModelPrs_ViewPort
  onMeasurementToolChange: (toolItem: string) => void
}

export const Measurement = ({
  scene,
  onMeasurementToolChange,
}: MeasurementProps) => {
  const [measurements] = useState<ModelPrs_View3dDimensionObject[]>([])
  const [measurementActive, setMeasurementActive] = useState('')
  const cn = useStyles()

  const measurementFactory = new cadex.ModelPrs_MeasurementFactory()
  const [selectedVertices, setSelectedVertices] = useState([])

  const handleMeasurementToolChange = (measurementType: string) => {
    setMeasurementActive(measurementType)
    onMeasurementToolChange(measurementType)
  }

  const onSelectedMergeVertices = (
    theEvent: ModelPrs_SelectionChangedEvent
  ) => {
    theEvent?.removed?.forEach((theRemoved) => {
      if (!theRemoved.shapes) {
        const anIndex = selectedVertices.indexOf(theRemoved.object)
        selectedVertices.splice(anIndex)
        return
      }

      const anObjectTransformation = theRemoved.object.transformation
      theRemoved.shapes.forEach((s) => {
        if (s instanceof cadex.ModelData_Vertex) {
          const aVertexWorldPosition = s.point.transformed(
            anObjectTransformation
          )
          const anIndex = selectedVertices.findIndex((v) =>
            v.point.isEqual(aVertexWorldPosition, 1e-5)
          )
          if (anIndex !== -1) {
            selectedVertices.splice(anIndex, 1)
          }
        }
      })
    })

    theEvent?.added?.forEach((added) => {
      if (!added.shapes) {
        selectedVertices.push(added.object)
        return
      }
      const anObjectTransformation = added.object.transformation

      added.shapes?.forEach((s) => {
        if (s instanceof cadex.ModelData_Vertex) {
          const aVertexWorldPosition = s.point.transformed(
            anObjectTransformation
          )
          const anIndex = selectedVertices.findIndex((v) =>
            v.point.isEqual(aVertexWorldPosition, 1e-5)
          )
          if (anIndex === -1) {
            selectedVertices.push(
              new cadex.ModelData_Vertex(aVertexWorldPosition)
            )
          }
        }
      })
    })

    setSelectedVertices([...selectedVertices])
  }

  const handleMeasureDimension = () => {
    setSelectedVertices([])
    scene.deselectAll()
    scene.globalSelectionMode = cadex.ModelPrs_SelectionMode.Vertex
    handleMeasurementToolChange(MeasurementType.Dimension)
  }

  const handleMeasureAngle = () => {
    setSelectedVertices([])
    scene.deselectAll()
    scene.globalSelectionMode = cadex.ModelPrs_SelectionMode.Vertex
    handleMeasurementToolChange(MeasurementType.Angle)
  }

  const clearMeasurence = () => {
    scene.deselectAll()
    scene.globalSelectionMode = cadex.ModelPrs_SelectionMode.Shape
    scene.remove(measurements)
    handleMeasurementToolChange('')
  }

  const measureDimension = (selectedVertices) => {
    if (selectedVertices.length !== 2) return

    const measurement = measurementFactory.createDistanceDimension(
      selectedVertices[0],
      selectedVertices[1]
    )

    measurement.setLengthDisplayUnits(cadex.Base_LengthUnit.Base_LU_Millimeters)

    scene.display(
      measurement,
      cadex.ModelPrs_DisplayMode.Shaded,
      cadex.ModelPrs_SelectionMode.Shape
    )

    measurements.push(measurement)
    scene.deselectAll()
    setSelectedVertices([])
  }

  const measureAngle = (selectedVertices) => {
    if (selectedVertices.length !== 3) return

    const measurement = measurementFactory.createAngleDimension(
      selectedVertices[0],
      selectedVertices[1],
      selectedVertices[2]
    )

    measurement.setAngleDisplayUnits(cadex.Base_AngleUnit.Base_AU_Degrees)

    scene.display(
      measurement,
      cadex.ModelPrs_DisplayMode.Shaded,
      cadex.ModelPrs_SelectionMode.Shape
    )

    measurements.push(measurement)
    scene.deselectAll()
    setSelectedVertices([])
  }

  useEffectOnce(() => {
    setSelectedVertices([])
    scene.removeEventListener('selectionChanged', onSelectedMergeVertices)
    scene.addEventListener('selectionChanged', onSelectedMergeVertices)
  })

  let timeoutId
  useEffect(() => {
    clearTimeout(timeoutId)
    // eslint-disable-next-line
    timeoutId = setTimeout(() => {
      if (!selectedVertices.length) return

      if (measurementActive === MeasurementType.Dimension) {
        measureDimension(selectedVertices)
        return
      }

      if (measurementActive === MeasurementType.Angle) {
        measureAngle(selectedVertices)
      }
    }, 100)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVertices])

  return (
    <div className={cn.root}>
      <nav style={{ position: 'relative' }}>
        <ul className={cn.container}>
          {!measurementActive && (
            <li className="active">
              <ActionIconButton>
                <StraightenOutlined />
                <Typography variant="caption">measure</Typography>
              </ActionIconButton>
            </li>
          )}

          <li
            className={
              measurementActive === MeasurementType.Dimension && 'active'
            }
            onClick={handleMeasureDimension}
          >
            <ActionIconButton>
              <StraightenOutlined />
              <Typography variant="caption">dimension</Typography>
            </ActionIconButton>
          </li>

          <li
            className={measurementActive === MeasurementType.Angle && 'active'}
            onClick={handleMeasureAngle}
          >
            <ActionIconButton>
              <FontAwesomeIcon icon={faDraftingCompass as IconProp} size={'lg'} />
              <Typography variant="caption">angle</Typography>
            </ActionIconButton>
          </li>

          {measurementActive && (
            <li onClick={clearMeasurence}>
              <ActionIconButton>
                <ClearOutlined />
                <Typography variant="caption">clear</Typography>
              </ActionIconButton>
            </li>
          )}
        </ul>
      </nav>
    </div>
  )
}
